/**
 * @file INCSUBPersnolDetails.js
 * @description
 * This component renders the personal details of a user in a read-only format.
 * It also provides an "Edit" option that toggles the update form (INCSUBUserDetailUpdate).
 * 
 * @props {Object} personalDetailData - Initial personal data passed as props (optional).
 * @props {Function} functionCall - Optional callback function triggered on updates.
 * 
 * @redux
 * - Uses Redux `useSelector` to access selectedUser and isUpdateDetails state.
 * - Uses Redux `useDispatch` to dispatch action for toggling edit mode (setIsUpdateDetails).
 * 
 * @dependencies
 * - React
 * - Redux
 * - CSS: stylePersnolDetails.css
 * @author govind dindewad
 */

import React from 'react';
import "./stylePersnolDetails.css"; 
import INCSUBUserDetailUpdate from './UserDetailUpdate';
import { useDispatch, useSelector } from 'react-redux';
import { setIsUpdateDetails } from '../../features/users/userSlice';

function INCSUBPersnolDetails({ personalDetailData: initialData, functionCall = null }) {
    // Access redux state
    const isUpdateDetails = useSelector((state) => state.user.isUpdateDetails);
    const personalDetailData = useSelector((state) => state.user.selectedUser);
    const dispatch = useDispatch();

    // Converts a date string into a more human-readable format
    const convertDateFormat = (dateStr) => {
        const dateObj = new Date(dateStr);
        const options = { day: '2-digit', month: 'long', year: 'numeric' };
        return dateObj.toLocaleDateString('en-GB', options);
    };

    return (
        <div className='INCSUBpersnoldetails'>
            <div className='INCSUBpersnoldetails_container'>
                {/* Header with title and Edit button */}
                <div className='INCSUBpersnoldetails_header_container'>
                    <div className='INCSUBpersnoldetails_header'>Personal Details</div>
                    <div
                        className='INCSUBpersnoldetails_edit_button'
                        onClick={() => dispatch(setIsUpdateDetails(true))}
                    >
                        {/* SVG edit icon */}
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_95_3353)">
                                <path d="M9.16699 3.33594H3.33366C2.89163 3.33594 2.46771 3.51153 2.15515 3.82409C1.84259 4.13665 1.66699 4.56058 1.66699 5.0026V16.6693C1.66699 17.1113 1.84259 17.5352 2.15515 17.8478C2.46771 18.1603 2.89163 18.3359 3.33366 18.3359H15.0003C15.4424 18.3359 15.8663 18.1603 16.1788 17.8478C16.4914 17.5352 16.667 17.1113 16.667 16.6693V10.8359" stroke="black" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M15.417 2.08027C15.7485 1.74875 16.1982 1.5625 16.667 1.5625C17.1358 1.5625 17.5855 1.74875 17.917 2.08027C18.2485 2.41179 18.4348 2.86143 18.4348 3.33027C18.4348 3.79911 18.2485 4.24875 17.917 4.58027L10.0003 12.4969L6.66699 13.3303L7.50033 9.99693L15.417 2.08027Z" stroke="black" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                            </g>
                            <defs>
                                <clipPath id="clip0_95_3353">
                                    <rect width="20" height="20" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>

                        Edit
                    </div>
                </div>

                {/* User Details Fields */}
                <div className='INCSUBpersnoldetails_feild_containers'>
                    {/* Individual Field Block */}
                    <div className='INCSUBpersnol_details_feild'>
                        <div className='INCSUBpersnol_details_feild_header'>Name</div>
                        <div className='INSUBpersnol_details_feild_value' title={personalDetailData?.userFullName}>
                            {personalDetailData?.userFullName}
                        </div>
                        <div className='INSUBpersnol_details_feild_footer_border' />
                    </div>

                    <div className='INCSUBpersnol_details_feild'>
                        <div className='INCSUBpersnol_details_feild_header'>User name</div>
                        <div className='INSUBpersnol_details_feild_value'>
                            {personalDetailData?.userName}
                        </div>
                        <div className='INSUBpersnol_details_feild_footer_border' />
                    </div>

                    <div className='INCSUBpersnol_details_feild'>
                        <div className='INCSUBpersnol_details_feild_header'>Type</div>
                        <div className='INSUBpersnol_details_feild_value'>
                            {personalDetailData?.userType}
                        </div>
                        <div className='INSUBpersnol_details_feild_footer_border' />
                    </div>

                    <div className='INCSUBpersnol_details_feild'>
                        <div className='INCSUBpersnol_details_feild_header'>Email Id</div>
                        <div className='INSUBpersnol_details_feild_value'>
                            {personalDetailData?.userEmail}
                        </div>
                        <div className='INSUBpersnol_details_feild_footer_border' />
                    </div>

                    <div className='INCSUBpersnol_details_feild'>
                        <div className='INCSUBpersnol_details_feild_header'>Phone Number</div>
                        <div className='INSUBpersnol_details_feild_value'>
                            +91 {personalDetailData?.userMobileNumber}
                        </div>
                        <div className='INSUBpersnol_details_feild_footer_border' />
                    </div>

                    {/* <div className='INCSUBpersnol_details_feild'>
                        <div className='INCSUBpersnol_details_feild_header'>Date of Birth</div>
                        <div className='INSUBpersnol_details_feild_value'>
                            {personalDetailData?.userDob ? convertDateFormat(personalDetailData?.userDob) : '-'}
                        </div>
                        <div className='INSUBpersnol_details_feild_footer_border' />
                    </div> */}
                </div>
            </div>

            {/* Conditional rendering of the Update Form */}
            {isUpdateDetails && (
                <INCSUBUserDetailUpdate
                    personalDetailData={personalDetailData}
                    functionCall={functionCall}
                    setIsUpdateDetails={setIsUpdateDetails}
                />
            )}
        </div>
    );
}

export default INCSUBPersnolDetails;
